"use client";

import React from "react";
import { PiTrashFill } from "react-icons/pi";
import Image from "next/image";
import Link from "next/link";
import { ICartItem } from "@/types/types";
import { buildProductShopUrl, getProductOptionByType } from "@/utils/helpers";
import { Button } from "../ui/button";
import CartCounter from "../cart-page/cart-counter";
import { useTranslations } from "next-intl";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "../ui/dialog";

type CartProductCardProps = {
  cartItem: ICartItem;
  onRemoveItem: (variantId: number) => void;
  onIncreaseItem: (variantId: number) => void;
  onDecreaseItem: (variantId: number) => void;
};

const CartProductCard = ({ cartItem, onRemoveItem, onIncreaseItem, onDecreaseItem }: CartProductCardProps) => {
  const t = useTranslations('ui');
  const { product, variant, quantity } = cartItem;
  const colorOption = getProductOptionByType(variant.options, 'color');
  const sizeOption = getProductOptionByType(variant.options, 'size');
  const shoeSize = getProductOptionByType(variant.options, 'shoe_size');

  return (
    <div className="flex items-start space-x-4">
      <Link
        href={buildProductShopUrl(product)}
        className="bg-[#F0EEED] rounded-lg w-full min-w-[100px] max-w-[100px] sm:max-w-[124px] aspect-square overflow-hidden"
      >
        <Image
          src={variant.image.url}
          alt={variant.image.alt}
          width={124}
          height={124}
          className="rounded-md w-full h-full object-cover hover:scale-110 transition-all duration-500"
          priority
        />
      </Link>
      <div className="flex w-full self-stretch flex-col">
        <div className="flex items-center justify-between">
          <Link
            href={buildProductShopUrl(product)}
            className="text-black font-bold text-base xl:text-xl line-clamp-2 md:line-clamp-1"
          >
            {product.name}
          </Link>
          <Button
            variant="ghost"
            size="icon"
            className="h-5 w-5 md:h-9 md:w-9"
            onClick={() => onRemoveItem(variant.id)}
          >
            <PiTrashFill className="text-xl md:text-2xl text-red-600" />
          </Button>
        </div>
        {sizeOption && (
          <div className="-mt-1">
            <span className="text-black text-xs md:text-sm mr-1">{t('size')}</span>
            <span className="text-black/60 text-xs md:text-sm">{sizeOption.value}</span>
          </div>
        )}

        {colorOption && (
          <div className="mb-auto -mt-1.5">
            <span className="text-black text-xs md:text-sm mr-1">{t('color')}:</span>
            <span className="text-black/60 text-xs md:text-sm">{colorOption.value}</span>
          </div>
        )}

        {shoeSize && (
          <div className="mb-auto -mt-1.5">
            <span className="text-black text-xs md:text-sm mr-1">{t('shoe_size')}:</span>
            <span className="text-black/60 text-xs md:text-sm">{shoeSize.value}</span>
            <Dialog>
              <DialogTrigger className="text-blue-600 hover:underline">
                {t('need_help')}
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{t('help_title')}</DialogTitle>
                  <DialogDescription>{t('help_description')}</DialogDescription>
                </DialogHeader>
                <div className="flex justify-end">
                  <Button onClick={() => { }}>{t('close')}</Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        )}

        <div className="flex items-center flex-wrap justify-between">
          <div className="flex items-center space-x-[5px] xl:space-x-2.5">
            {product.price.discount.percentage > 0 ? (
              <span className="font-bold text-black text-xl xl:text-2xl">
                {`${product.price.current}`}
              </span>
            ) : product.price.discount.amount > 0 ? (
              <span className="font-bold text-black text-xl xl:text-2xl">
                {`$${product.price.initial}`}
              </span>
            ) : (
              <span className="font-bold text-black text-xl xl:text-2xl">
                ${product.price.current}
              </span>
            )}
            {product.price.discount.percentage > 0 && (
              <span className="font-bold text-black/40 line-through text-xl xl:text-2xl">
                ${product.price.current}
              </span>
            )}
            {product.price.discount.amount > 0 && (
              <span className="font-bold text-black/40 line-through text-xl xl:text-2xl">
                ${product.price.current}
              </span>
            )}
            {product.price.discount.percentage > 0 ? (
              <span className="font-medium text-[10px] xl:text-xs py-1.5 px-3.5 rounded-full bg-[#FF3333]/10 text-[#FF3333]">
                {`-${product.price.discount.percentage}%`}
              </span>
            ) : (
              product.price.discount.amount > 0 && (
                <span className="font-medium text-[10px] xl:text-xs py-1.5 px-3.5 rounded-full bg-[#FF3333]/10 text-[#FF3333]">
                  {`-$${product.price.discount.amount}`}
                </span>
              )
            )}
          </div>
          <CartCounter
            initialValue={quantity}
            onAdd={() => onIncreaseItem(variant.id)}
            onRemove={() => {
              if (quantity > 1) {
                onDecreaseItem(variant.id);
              } else {
                onRemoveItem(variant.id);
              }
            }}
            isZeroDelete
            className="px-5 py-3 max-h-8 md:max-h-10 min-w-[105px] max-w-[105px] sm:max-w-32"
          />
        </div>
      </div>
    </div>
  );
}

export default CartProductCard;